import { useState } from "react";
import { ShieldCheck, ShieldAlert, Gauge, Loader2, Flag, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const FIT = {
  strong: { text: "Strong fit", cls: "text-brand border-brand/40 bg-brand-soft" },
  good: { text: "Good fit", cls: "text-info border-info/40 bg-info-soft" },
  stretch: { text: "Stretch", cls: "text-warning border-warning/40 bg-warning-soft" },
};
const POST = {
  job: "Looks like a genuine job post",
  scam: "This looks like a possible scam: it may ask for fees or personal details",
  not_job: "This doesn't look like a job posting",
};

export default function PrecheckCard({ checking, data, askOverride, onOverride, jdText, company, onCompanyChange, onReported }) {
  const [reporting, setReporting] = useState(false);
  if (!checking && !data) return null;
  const report = async () => {
    setReporting(true);
    try {
      await api.post("/jobs/report-scam", { jd_text: jdText, company });
      toast.success("Thanks for reporting. We'll warn others who see this post.");
      onReported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send your report");
    } finally {
      setReporting(false);
    }
  };
  const post = data?.post;
  const fit = data?.fit;
  const flagged = data?.flagged;
  return (
    <div data-testid="job-precheck-card" className={`rounded-xl border p-4 af-fade-up ${flagged ? "border-warning/40 bg-warning-soft" : "border-line bg-surface"}`}>
      {checking ? (
        <div className="flex items-center gap-2 text-sm text-body"><Loader2 className="w-4 h-4 animate-spin" /> Checking this job…</div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          {post && (
            <span data-testid="job-precheck-post" data-verdict={post.label} className={`flex items-center gap-2 text-sm ${flagged ? "text-warning" : "text-body"}`}>
              {flagged || data.you_reported ? <ShieldAlert className={`w-4 h-4 ${flagged ? "" : "text-warning"}`} /> : <ShieldCheck className="w-4 h-4 text-brand" />}
              {flagged ? (post.label === "job" ? "Reported as a scam by other users" : POST[post.label])
                : data.you_reported ? "You reported this as a possible scam" : POST.job}
            </span>
          )}
          {flagged && (
            <a href="/scam-guide" target="_blank" rel="noreferrer" data-testid="job-precheck-scam-guide-link" className="text-sm text-warning underline underline-offset-2 hover:text-warning">
              How to spot job scams
            </a>
          )}
          {fit && !flagged && FIT[fit.label] && (
            <span data-testid="job-precheck-fit" data-fit={fit.label} className={`ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${FIT[fit.label].cls}`}>
              <Gauge className="w-3.5 h-3.5" /> Your fit: {FIT[fit.label].text}
            </span>
          )}
        </div>
      )}
      {data?.reported && post?.label !== "job" && (
        <div data-testid="job-precheck-reported" className="mt-3 flex items-center gap-2 text-sm text-warning">
          <Users className="w-4 h-4" /> Reported as a scam by other users
        </div>
      )}
      {!checking && data && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <label className="flex items-center gap-2 text-xs text-faint">
            Company
            <input data-testid="job-precheck-company-input" value={company || ""} onChange={(e) => onCompanyChange?.(e.target.value)}
              placeholder="Not detected" maxLength={200}
              className="bg-canvas border border-line-strong rounded-full px-3 py-1 text-xs text-ink w-44 focus:outline-none focus:border-brand" />
          </label>
          {data.you_reported ? (
            <span data-testid="job-precheck-you-reported" className="text-xs text-faint">Thanks, your report helps warn others.</span>
          ) : (
            <button data-testid="job-precheck-report-button" onClick={report} disabled={reporting} className="flex items-center gap-1.5 text-xs text-body hover:text-warning transition-colors disabled:opacity-50">
              {reporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Flag className="w-3.5 h-3.5" />} Report as scam
            </button>
          )}
        </div>
      )}
      {flagged && askOverride && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-body">
          <span>Tailoring uses a full AI run. Are you sure this is a real job?</span>
          <button data-testid="job-precheck-override" onClick={onOverride} className="px-4 py-1.5 rounded-xl border border-warning/50 text-warning hover:bg-warning-soft transition-colors">
            Tailor anyway
          </button>
        </div>
      )}
    </div>
  );
}
